import React from "react";
import LegalLayout, { Section, P, UL, LI, Callout } from "./LegalLayout";

export default function RefundPolicy() {
  return (
    <LegalLayout
      code="DOC · 03 / REFUND POLICY"
      title="Refund Policy"
      lastUpdated="December 2025"
    >
      <Section id="overview" title="1. Overview">
        <P>
          Sebyx Academy is structured across Entry, Pro, and Elite tracks. Each
          track has a different delivery model, and therefore a different refund
          treatment, set out below. We aim to be fair, clear, and consistent
          with Indian consumer protection norms and applicable payment
          provider guidelines.
        </P>
      </Section>

      <Section id="entry" title="2. Entry Tier — Frontline Elite / Rookie Hunter (₹3,999 – ₹14,999)">
        <P>
          Our Entry tracks are cohort-based programmes with limited seats
          (online live, F2F weekend, or 1-on-1). Because we block faculty time
          and a seat for you, refunds are time-bound:
        </P>
        <UL>
          <LI>
            <strong>Full refund (100%)</strong> if cancellation is requested at
            least 7 days before the cohort start date.
          </LI>
          <LI>
            <strong>50% refund</strong> if cancellation is requested between 7
            days and 48 hours before start.
          </LI>
          <LI>
            <strong>No refund</strong> once the cohort has commenced or the
            first live session has been attended.
          </LI>
          <LI>
            If Sebyx cancels or reschedules the cohort, you receive a full
            refund or a credit toward a future cohort — your choice.
          </LI>
          <LI>
            Requests must be sent to{" "}
            <a
              href="mailto:contact@sebyx.com"
              className="text-sebyx-accent hover:underline"
            >
              contact@sebyx.com
            </a>{" "}
            from the buyer's registered email.
          </LI>
        </UL>
      </Section>

      <Section id="pro" title="3. Pro Tier — Master Hunter Certification (₹7,499)">
        <P>
          The Pro tier is a 4-week live cohort with limited seats. Because we
          block faculty time and a seat for you, refunds are time-bound:
        </P>
        <UL>
          <LI>
            <strong>Full refund (100%)</strong> if cancellation is requested at
            least 7 days before the cohort start date.
          </LI>
          <LI>
            <strong>Partial refund (50%)</strong> if cancellation is requested
            between 6 days and 24 hours before the cohort start date.
          </LI>
          <LI>
            <strong>No refund</strong> after the cohort has commenced.
          </LI>
          <LI>
            If Sebyx postpones or cancels a cohort for any reason, you will
            receive either a full refund or a free transfer to the next
            available cohort, at your discretion.
          </LI>
          <LI>
            Missed live sessions are <strong>not</strong> grounds for refund.
            Recordings of live sessions are made available to enrolled
            participants for catch-up.
          </LI>
        </UL>
      </Section>

      <Section id="elite" title="4. Elite Tier — Executive 1-on-1 Mentorship (₹24,999)">
        <P>
          The Elite tier is delivered as a personalized 30-day intensive. Given
          the bespoke preparation involved:
        </P>
        <UL>
          <LI>
            <strong>Full refund</strong> if requested before the kickoff
            session.
          </LI>
          <LI>
            <strong>Pro-rata refund</strong> for unused weeks if cancellation
            is requested within the first 7 days of kickoff.
          </LI>
          <LI>
            <strong>No refund</strong> after Day 7 of the engagement.
          </LI>
          <LI>
            Sessions may be rescheduled with at least 24 hours' notice without
            penalty.
          </LI>
        </UL>
      </Section>

      <Section id="bdaas" title="5. BDaaS & Fractional Engagements">
        <P>
          Fractional Leadership and consulting engagements are governed by the
          specific Statement of Work signed with the client. Refund or
          early-termination terms are defined in that SOW and supersede this
          general policy.
        </P>
      </Section>

      <Section id="how" title="6. How to Request a Refund">
        <P>
          Send an email to{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>{" "}
          with the subject line "Refund Request — [Tier Name]" and include:
        </P>
        <UL>
          <LI>The name and email used at purchase</LI>
          <LI>The Razorpay payment ID (begins with <code>pay_…</code>)</LI>
          <LI>A short reason for the refund request</LI>
        </UL>
        <P>
          We acknowledge every request within 2 working days and decision
          within 7 working days.
        </P>
      </Section>

      <Section id="processing" title="7. Refund Processing Time">
        <Callout>
          Approved refunds are issued back to the original payment instrument
          via Razorpay. Once initiated, banks typically credit the amount
          within <strong>5–10 working days</strong>. Sebyx is not liable for
          delays caused by your card issuer or bank.
        </Callout>
      </Section>

      <Section id="contact" title="8. Contact">
        <P>
          Questions about a refund? Write to{" "}
          <a
            href="mailto:contact@sebyx.com"
            className="text-sebyx-accent hover:underline"
          >
            contact@sebyx.com
          </a>{" "}
          or call{" "}
          <a
            href="tel:+919108436667"
            className="text-sebyx-accent hover:underline"
          >
            +91 91084 36667
          </a>
          .
        </P>
      </Section>
    </LegalLayout>
  );
}
